// src/pages/ConsultaDetailPage.jsx
//La consulta ya guardada

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  Paper, Typography, Box, Button, Divider, Accordion, 
  AccordionSummary, AccordionDetails, Grid, CircularProgress,
  List, ListItem, ListItemText, ListItemIcon, IconButton, Link,
  Fade, Chip, Avatar, Card, CardContent, Tooltip, Badge, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, FormLabel, RadioGroup, Radio, FormControlLabel, FormHelperText, MenuItem
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DescriptionIcon from '@mui/icons-material/Description';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import MedicationIcon from '@mui/icons-material/Medication';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import BiotechIcon from '@mui/icons-material/Biotech';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import HealingIcon from '@mui/icons-material/Healing';
import StarIcon from '@mui/icons-material/Star';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import EventNoteIcon from '@mui/icons-material/EventNote';
import PersonIcon from '@mui/icons-material/Person';
import MonitorWeightIcon from '@mui/icons-material/MonitorWeight';
import HeightIcon from '@mui/icons-material/Height';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AirIcon from '@mui/icons-material/Air';
import SpeedIcon from '@mui/icons-material/Speed';
import axiosInstance from '../axiosInstance';
import parse from 'html-react-parser';
import { useSubscriptionFeatures } from '../hooks/useSubscriptionFeatures';
import { alpha } from '@mui/material/styles';
import {
  softShadow, softShadowHover, cardShadow, radius,
  sectionTone, sectionAccordionSx, sectionSummarySx, sectionDetailsSx,
  sectionIconBoxSx, heroSurfaceSx, statCardSx,
} from './consultaTheme';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://app-medifyhc.com';

const ConsultaDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [consulta, setConsulta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fadeIn, setFadeIn] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailOption, setEmailOption] = useState('registered'); // 'registered' | 'custom'
  const [customEmail, setCustomEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const patientEmail =
    consulta?.paciente_email ||
    consulta?.email_paciente ||
    consulta?.paciente_correo ||
    consulta?.correo_paciente ||
    '';
  
  // Estados para Interconsulta/Referencia
  const [interconsultaOpen, setInterconsultaOpen] = useState(false);
  const [interconsultaData, setInterconsultaData] = useState({ nombreDoctor: '', especialidad: '', historiaEnfermedad: '', tratamiento: '', email: '' });
  const [generandoRefPDF, setGenerandoRefPDF] = useState(false);
  const [enviandoRefEmail, setEnviandoRefEmail] = useState(false);
  
  
  // Verificar características de suscripción
  const { hasFinanzasAccess, loading: featuresLoading } = useSubscriptionFeatures();
  
  // Determinar si el usuario tiene acceso a funciones premium
  // Asumimos que si tiene acceso a finanzas, es un plan premium
  const hasPremiumAccess = hasFinanzasAccess;
  
  const [expanded, setExpanded] = useState({
    anamnesis: true,
    diagnosticos: true,
    signosVitales: false,
    antecedentes: false,
    examenFisico: false,
    reportes: false,
    laboratoriales: false,
    imagen: false,
    plan: false,
    receta: true,
  });

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(prev => ({ ...prev, [panel]: isExpanded }));
  };

  // Rol de color Material 3 por sección (color-codificación)
  const SECTION_ROLE = {
    signosVitales: 'error',
    antecedentes: 'warning',
    anamnesis: 'info',
    examenFisico: 'primary',
    diagnosticos: 'error',
    reportes: 'secondary',
    laboratoriales: 'success',
    imagen: 'info',
    plan: 'primary',
    receta: 'secondary',
  };

  // Componente para mostrar el badge premium
  const PremiumFeatureBadge = ({ children }) => (
    <Tooltip 
      title={
        <Box sx={{ p: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Función Premium
          </Typography>
          <Typography variant="body2">
            Esta función requiere un plan premium mensual o anual.
          </Typography>
          <Button 
            variant="contained" 
            size="small" 
            fullWidth 
            component={RouterLink} 
            to="/planes" 
            sx={{ mt: 1 }}
          >
            Ver Planes
          </Button>
        </Box>
      } 
      arrow
      placement="top"
    >
      <Box sx={{ position: 'relative' }}>
        {children}
        <Avatar 
          sx={{ 
            position: 'absolute', 
            top: -10, 
            right: -10, 
            width: 22, 
            height: 22, 
            bgcolor: 'warning.main',
            border: '2px solid white'
          }}
        >
          <StarIcon sx={{ fontSize: 14 }} />
        </Avatar>
      </Box>
    </Tooltip>
  );

  // Encabezado color-codificado reutilizable para las secciones (Material 3)
  const SectionHeader = ({ icon: Icon, title, role = 'primary', chip = null }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
      <Box sx={(theme) => sectionIconBoxSx(theme, role)}>
        <Icon />
      </Box>
      <Typography variant="h6" fontWeight={700} sx={{ letterSpacing: -0.2 }}>{title}</Typography>
      {chip}
    </Box>
  );

  const fetchConsulta = () => {
    setLoading(true);
    axiosInstance.get(`/consultas/${id}/`)
      .then(response => {
        setConsulta(response.data);
        setLoading(false);
        // Activar animación después de cargar los datos
        setTimeout(() => setFadeIn(true), 100);
      })
      .catch(error => {
        console.error("Error al cargar la consulta:", error);
        toast.error("No se pudo cargar la consulta.");
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchConsulta();
  }, [id]);

  const handleDelete = async () => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta consulta?')) {
      try {
        await axiosInstance.delete(`/consultas/${id}/`);
        toast.success('Consulta eliminada con éxito.');
        navigate(`/pacientes/${consulta.paciente_id}`);
      } catch (error) {
        toast.error('No se pudo eliminar la consulta.');
      }
    }
  };
  
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Mostrar toast de carga
    const loadingToast = toast.loading("Subiendo archivo...");
    
    const formData = new FormData();
    formData.append('archivo', file);
    formData.append('consulta', id);
    try {
      await axiosInstance.post('/reportes-consulta/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.update(loadingToast, { 
        render: "Archivo subido correctamente", 
        type: "success", 
        isLoading: false,
        autoClose: 3000
      });
      fetchConsulta();
    } catch (error) {
      console.error("Error al subir el archivo:", error.response?.data);
      toast.update(loadingToast, { 
        render: "No se pudo subir el archivo", 
        type: "error", 
        isLoading: false,
        autoClose: 3000
      });
    }
  };

  // Convierte a URL absoluta si viene relativa (/media/..)
  // Usa VITE_API_URL o el origin actual del navegador
  const toAbsoluteUrl = (url) => {
    if (!url) return '';
    if (/^https?:\/\//i.test(url)) return url; // ya es absoluta
    const base = (import.meta.env.VITE_API_URL || window.location.origin).replace(/\/+$/, '');
    const path = url.startsWith('/') ? url : `/${url}`;
    return `${base}${path}`;
  };


  const handleDeleteFile = async (reporteId) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este archivo?')) {
      try {
        await axiosInstance.delete(`/reportes-consulta/${reporteId}/`);
        toast.success('Archivo eliminado.');
        fetchConsulta();
      } catch (error) {
        toast.error('No se pudo eliminar el archivo.');
      }
    }
  };

  const handleFileDownload = (fileUrl, fileName) => {
    const loadingToast = toast.loading("Descargando archivo...");
    const finalUrl = toAbsoluteUrl(fileUrl);

    axiosInstance
      .get(finalUrl, { responseType: 'blob' })
      .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);

        toast.update(loadingToast, {
          render: "Archivo descargado correctamente",
          type: "success",
          isLoading: false,
          autoClose: 3000
        }); 
      })
      .catch(() => {
        toast.update(loadingToast, {
          render: "No se pudo descargar el archivo",
          type: "error",
          isLoading: false,
          autoClose: 3000
        });
      });  
    };
   
  // Función para descargar la receta en PDF
  const recetaTags = React.useMemo(() => {
    const tags = new Set();
    (consulta?.medicamentos || []).forEach(m => {
      const tag = (m.receta_tag || 'general').trim().toLowerCase();
      tags.add(tag);
    });
    return Array.from(tags);
  }, [consulta]);
  const [selectedTagPdf, setSelectedTagPdf] = useState('general');


  const downloadRecetaPDF = () => {
    const loadingToast = toast.loading("Generando PDF de receta...");
    const t = (selectedTagPdf || '').trim().toLowerCase();
    const qs = new URLSearchParams();
    if (t) qs.set('receta_tag', t);    // sin no_signature

    axiosInstance.get(`/consultas/${id}/generar-receta/${qs.toString() ? `?${qs}` : ''}`, { responseType: 'blob' })
    .then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receta_${id}${t ? `-${t}` : ''}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      
      toast.update(loadingToast, {
        render: "Receta PDF descargada correctamente",
        type: "success",
        isLoading: false,
        autoClose: 3000
      });
    })
    .catch(error => {
      console.error("Error al descargar la receta PDF:", error); 
      toast.update(loadingToast, {
        render: error.response?.status === 403 
          ? "Esta función requiere un plan premium" 
          : "Error al generar la receta PDF",
        type: "error",
        isLoading: false,
        autoClose: 5000
      });
    });
  };

  const openEmailDialog = () => {
    setEmailError('');
    setCustomEmail('');
    // Si no hay email del paciente, forzamos la opción custom
    setEmailOption(patientEmail ? 'registered' : 'custom');
    setEmailDialogOpen(true);
  };

  const closeEmailDialog = () => {
    if (sendingEmail) return;
    setEmailDialogOpen(false);
  };

  const validateEmail = (email) => {
    // Validación simple
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim());
  };

  const handleSendRecetaEmail = async () => {
    setEmailError('');
    let target = '';

    if (emailOption === 'registered') {
      if (!patientEmail) {
        setEmailError('El paciente no tiene un correo registrado.');
        return;
      }
      target = patientEmail;
    } else {
      if (!validateEmail(customEmail)) {
        setEmailError('Ingresa un correo válido.');
        return;
      }
      target = customEmail.trim();
    }

    setSendingEmail(true);
    const toastId = toast.loading('Enviando receta por correo...');

    try {
      // Ajusta este endpoint según tu backend
      // Sugerencia: POST /consultas/:id/enviar-receta/ con body { email: "..." }
      await axiosInstance.post(`/consultas/${id}/enviar-receta/`, { email: target });

      toast.update(toastId, {
        render: 'Receta enviada por correo',
        type: 'success',
        isLoading: false,
        autoClose: 3000
      });
      setEmailDialogOpen(false);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        'No se pudo enviar la receta por correo.';
      toast.update(toastId, { render: msg, type: 'error', isLoading: false, autoClose: 5000 });
    } finally {
      setSendingEmail(false);
    }
  };


// Función para imprimir la consulta
const printConsulta = () => {
  const loadingToast = toast.loading("Generando PDF de consulta...");
  
  axiosInstance.get(`/consultas/${id}/imprimir/`, {
    responseType: 'blob',
  })
  .then(response => {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `consulta_${id}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    
    toast.update(loadingToast, {
      render: "Consulta PDF descargada correctamente",
      type: "success",
      isLoading: false,
      autoClose: 3000
    });
  })
  .catch(error => {
    console.error("Error al descargar la consulta PDF:", error);
    
    toast.update(loadingToast, {
      render: error.response?.status === 403 
        ? "Esta función requiere un plan premium" 
        : "Error al generar la consulta PDF",
      type: "error",
      isLoading: false,
      autoClose: 5000
    });
  });
};

  // Función para formatear la fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '80vh' 
      }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
          Cargando consulta médica...
        </Typography>
      </Box>
    );
  }

  if (!consulta) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h5" color="error">
          No se pudo cargar la información de la consulta
        </Typography>
        <Button 
          variant="contained" 
          component={RouterLink} 
          to={`/pacientes/${consulta?.paciente_id || ''}`} 
          sx={{ mt: 2 }}
          startIcon={<ArrowBackIcon />}
        >
          Volver al paciente
        </Button>
      </Box>
    );
  }

  return (
    <Box p={{ xs: 2, md: 4 }} sx={{ maxWidth: '1600px', margin: '0 auto' }}>
      {/* Botón para volver */}
      <Button 
        variant="text" 
        component={RouterLink} 
        to={`/pacientes/${consulta.paciente_id}`} 
        startIcon={<ArrowBackIcon />}
        sx={{ 
          mb: 3,
          borderRadius: 999,
          px: 2,
          color: 'text.secondary',
          transition: 'all 0.2s ease',
          '&:hover': {
            color: 'primary.main',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            transform: 'translateX(-4px)'
          }
        }}
      >
        Volver al Paciente
      </Button>

      {/* Cabecera: Título y Botones */}
      <Fade in={fadeIn} timeout={800}>
        <Paper 
          elevation={0}
          sx={(theme) => ({
            ...heroSurfaceSx(theme, 'primary'),
            p: { xs: 2.5, md: 3.5 },
            mb: 4,
          })}
        >
          {/* Círculo decorativo M3 */}
          <Box sx={(theme) => ({
            position: 'absolute', top: -70, right: -50, width: 220, height: 220,
            borderRadius: '50%', pointerEvents: 'none',
            background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.14)} 0%, transparent 70%)`,
          })} />

          <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            {/* Contenedor para el título y el ícono */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar 
                sx={(theme) => ({ 
                  bgcolor: 'primary.main', 
                  width: 64, 
                  height: 64,
                  mr: 2.5,
                  borderRadius: `${radius.md}px`,
                  boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.4)}`,
                })}
                variant="rounded"
              >
                <AssignmentIcon sx={{ fontSize: 34 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" fontWeight={800} sx={{ mb: 0.75, letterSpacing: -0.5 }}>
                  Consulta Médica
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip
                    icon={<EventNoteIcon />}
                    label={formatDate(consulta.fecha_hora_consulta)}
                    size="small"
                    sx={(theme) => ({
                      fontWeight: 600,
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                      color: 'primary.main',
                      border: '1px solid',
                      borderColor: alpha(theme.palette.primary.main, 0.24),
                      '& .MuiChip-icon': { color: 'primary.main' },
                    })}
                  />
                </Box>
              </Box>
            </Box>

            {/* Contenedor para TODOS los botones */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {consulta.paciente_id && (
                <Button
                  variant="outlined"
                  color="info"
                  component={RouterLink}
                  to={`/doctor/pacientes/${consulta.paciente_id}/crecimiento`}
                  startIcon={<ChildCareIcon />}
                  sx={{
                    borderRadius: 999,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                    }
                  }}
                >
                  Tablas
                </Button>
              )}

              {/* Botón de Lab PDF con control de acceso */}
              {hasPremiumAccess ? (
                <Button 
                  variant="outlined" 
                  startIcon={<PictureAsPdfIcon />} 
                  onClick={() => {
                    const loadingToast = toast.loading("Generando PDF de exámenes...");
                    axiosInstance
                      .get(`/consultas/${id}/examenes-lab-pdf/`, { responseType: 'blob' })
                      .then(response => {
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', `examenes_lab_${id}.pdf`);
                        document.body.appendChild(link);
                        link.click();
                        link.parentNode.removeChild(link);
                        toast.update(loadingToast, {
                          render: "PDF de exámenes descargado",
                          type: "success",
                          isLoading: false,
                          autoClose: 3000
                        });
                      })
                      .catch(error => {
                        console.error("Error al descargar Exámenes Lab PDF:", error);
                        toast.update(loadingToast, {
                          render: error.response?.status === 403 
                            ? "Esta función requiere un plan premium"
                            : "Error al generar el PDF de exámenes",
                          type: "error",
                          isLoading: false,
                          autoClose: 5000
                        });
                      });
                  }}                 
                  sx={{
                    borderRadius: 999,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                    }
                  }}
                >
                  Exámenes Lab PDF
                </Button>
              ) : (
                <PremiumFeatureBadge>
                  <Button 
                    variant="outlined" 
                    startIcon={<PictureAsPdfIcon />} 
                    disabled
                    sx={{ borderRadius: 999 }}
                  >
                    Exámenes Lab PDF
                  </Button>
                </PremiumFeatureBadge>
              )}
              
              {/* Botón de Imprimir Consulta con control de acceso */}
              {hasPremiumAccess ? (
                <Button 
                  variant="outlined" 
                  color="secondary"
                  startIcon={<PrintIcon />} 
                  onClick={printConsulta}
                  sx={{
                    borderRadius: 999,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                    }
                  }}
                >
                  Imprimir Consulta
                </Button>
              ) : (
                <PremiumFeatureBadge>
                  <Button 
                    variant="outlined" 
                    color="secondary"
                    startIcon={<PrintIcon />} 
                    disabled
                    sx={{ borderRadius: 999 }}
                  >
                    Imprimir Consulta
                  </Button>
                </PremiumFeatureBadge>
              )}
              
              {/* Botones de Editar y Eliminar (sin restricción) */}
              <Button 
                variant="contained" 
                component={RouterLink} 
                to={`/consultas/${id}/edit/${consulta.paciente_id}`}
                startIcon={<EditIcon />}
                sx={{
                  borderRadius: 999,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                  }
                }}
              >
                Editar
              </Button>
              <Button 
                variant="outlined" 
                color="error" 
                onClick={handleDelete}
                startIcon={<DeleteIcon />}
                sx={{
                  borderRadius: 999,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                    bgcolor: 'error.light',
                    color: 'white',
                    borderColor: 'error.light',
                  }
                }}
              >
                Eliminar
              </Button>
            </Box>
          </Box>  
          
          {/* Información del paciente y médico — tarjetas tonales M3 */}
          <Grid container spacing={2} sx={{ position: 'relative' }}>
            <Grid item xs={12} md={6}>
              <Box sx={(theme) => ({
                display: 'flex', alignItems: 'center', gap: 1.5, p: 2,
                borderRadius: `${radius.sm}px`,
                bgcolor: alpha(theme.palette.background.paper, 0.7),
                border: '1px solid',
                borderColor: alpha(theme.palette.divider, 0.6),
                backdropFilter: 'blur(4px)',
              })}>
                <Box sx={(theme) => sectionIconBoxSx(theme, 'success')}>
                  <PersonIcon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2, letterSpacing: 0.6 }}>
                    Paciente
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={700} noWrap>
                    {consulta.paciente}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={(theme) => ({
                display: 'flex', alignItems: 'center', gap: 1.5, p: 2,
                borderRadius: `${radius.sm}px`,
                bgcolor: alpha(theme.palette.background.paper, 0.7),
                border: '1px solid',
                borderColor: alpha(theme.palette.divider, 0.6),
                backdropFilter: 'blur(4px)',
              })}>
                <Box sx={(theme) => sectionIconBoxSx(theme, 'info')}>
                  <MedicalInformationIcon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2, letterSpacing: 0.6 }}>
                    Atendido por
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={700} noWrap>
                    {consulta.medico}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Fade>

      {/* Secciones de la consulta */}
      <Fade in={fadeIn} timeout={1000}>
        <Box>
          {/* Signos Vitales */}
          <Accordion 
            expanded={expanded.signosVitales} 
            onChange={handleAccordionChange('signosVitales')}
            sx={(theme) => sectionAccordionSx(theme, 'error', expanded.signosVitales)}
          >
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />}
              sx={(theme) => sectionSummarySx(theme, 'error', expanded.signosVitales)}
            >
              <SectionHeader icon={MonitorHeartIcon} title="Signos Vitales" role="error" />
            </AccordionSummary>
            <AccordionDetails sx={(theme) => sectionDetailsSx(theme, 'error')}>
              <Grid container spacing={2}>
                {[
                  { label: 'Presión Arterial', value: consulta.presion_arterial, icon: SpeedIcon, role: 'error' },
                  { label: 'Pulso', value: consulta.pulso, icon: FavoriteIcon, role: 'error' },
                  { label: 'Frec. Cardiaca', value: consulta.frecuencia_cardiaca, icon: FavoriteIcon, role: 'error' },
                  { label: 'Temperatura', value: consulta.temperatura, icon: ThermostatIcon, role: 'warning' },
                  { label: 'SatO2', value: consulta.saturacion_oxigeno, icon: AirIcon, role: 'info' },
                  { label: 'Peso', value: consulta.peso, icon: MonitorWeightIcon, role: 'success' },
                  { label: 'Altura', value: consulta.altura, icon: HeightIcon, role: 'success' },
                ].map((v) => (
                  <Grid item xs={6} sm={4} md={3} key={v.label}>
                    <Box sx={(theme) => statCardSx(theme, v.role)}>
                      <Box sx={(theme) => ({ ...sectionIconBoxSx(theme, v.role), width: 38, height: 38, mr: 0 })}>
                        <v.icon fontSize="small" />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                          {v.label}
                        </Typography>
                        <Typography variant="subtitle1" fontWeight={700} noWrap
                          sx={{ color: v.value ? 'text.primary' : 'text.disabled' }}>
                          {v.value || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Antecedentes Clínicos */}
          <Accordion 
            expanded={expanded.antecedentes} 
            onChange={handleAccordionChange('antecedentes')}
            sx={(theme) => sectionAccordionSx(theme, SECTION_ROLE['antecedentes'], expanded.antecedentes)}
          >
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />}
              sx={(theme) => sectionSummarySx(theme, SECTION_ROLE['antecedentes'], expanded.antecedentes)}
            >
              <SectionHeader icon={HistoryEduIcon} title="Antecedentes Clínicos" role={SECTION_ROLE['antecedentes']} />
            </AccordionSummary>
            <AccordionDetails sx={(theme) => sectionDetailsSx(theme, SECTION_ROLE['antecedentes'])}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, pl: 1.5, borderLeft: (theme) => `3px solid ${theme.palette.primary.main}`, color: "primary.main" }}>
                  Antecedentes Patológicos
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    pl: 2,
                    whiteSpace: 'pre-wrap',
                    fontStyle: consulta.antecedentes_patologicos ? 'normal' : 'italic',
                    color: consulta.antecedentes_patologicos ? 'text.primary' : 'text.secondary',
                  }}
                >
                  {consulta.antecedentes_patologicos || 'No registrados'}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, pl: 1.5, borderLeft: (theme) => `3px solid ${theme.palette.primary.main}`, color: "primary.main" }}>
                  Antecedentes Inmuno-Alérgicos
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    pl: 2,
                    whiteSpace: 'pre-wrap',
                    fontStyle: consulta.antecedentes_alergicos ? 'normal' : 'italic',
                    color: consulta.antecedentes_alergicos ? 'text.primary' : 'text.secondary',
                  }}
                >
                  {consulta.antecedentes_alergicos || 'No registrados'}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, pl: 1.5, borderLeft: (theme) => `3px solid ${theme.palette.primary.main}`, color: "primary.main" }}>
                  Antecedentes Quirúrgicos
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    pl: 2,
                    whiteSpace: 'pre-wrap',
                    fontStyle: consulta.antecedentes_quirurgicos ? 'normal' : 'italic',
                    color: consulta.antecedentes_quirurgicos ? 'text.primary' : 'text.secondary',
                  }}
                >
                  {consulta.antecedentes_quirurgicos || 'No registrados'}
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Anamnesis */}
          <Accordion 
            expanded={expanded.anamnesis} 
            onChange={handleAccordionChange('anamnesis')}
            sx={(theme) => sectionAccordionSx(theme, SECTION_ROLE['anamnesis'], expanded.anamnesis)}
          >
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />}
              sx={(theme) => sectionSummarySx(theme, SECTION_ROLE['anamnesis'], expanded.anamnesis)}
            >
              <SectionHeader icon={MedicalInformationIcon} title="Anamnesis (Padecimiento Actual)" role={SECTION_ROLE['anamnesis']} />
            </AccordionSummary>
            <AccordionDetails sx={(theme) => sectionDetailsSx(theme, SECTION_ROLE['anamnesis'])}>
              {consulta.anamnesis ? (
                <Box 
                  sx={{ 
                    whiteSpace: 'pre-wrap', 
                    pl: 2,
                    lineHeight: 1.7,
                    color: 'text.primary',
                    '& p': {
                      margin: '0.5em 0',
                    },
                    '& strong': {
                      fontWeight: 600,
                    }
                  }}
                >
                  {parse(consulta.anamnesis)}
                </Box>
              ) : (
                <Typography 
                  variant="body1" 
                  sx={{ 
                    whiteSpace: 'pre-wrap', 
                    pl: 2,
                    lineHeight: 1.7,
                    fontStyle: 'italic',
                    color: 'text.secondary',
                  }}
                >
                  No registrado
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Examen Físico */}
          <Accordion 
            expanded={expanded.examenFisico} 
            onChange={handleAccordionChange('examenFisico')}
            sx={(theme) => sectionAccordionSx(theme, SECTION_ROLE['examenFisico'], expanded.examenFisico)}
          >
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />}
              sx={(theme) => sectionSummarySx(theme, SECTION_ROLE['examenFisico'], expanded.examenFisico)}
            >
              <SectionHeader icon={LocalHospitalIcon} title="Examen Físico" role={SECTION_ROLE['examenFisico']} />
            </AccordionSummary>
            <AccordionDetails sx={(theme) => sectionDetailsSx(theme, SECTION_ROLE['examenFisico'])}>
              {consulta.examen_fisico ? (
                <Box 
                  sx={{ 
                    whiteSpace: 'pre-wrap', 
                    pl: 2,
                    lineHeight: 1.7,
                    color: 'text.primary',
                    '& p': { // Estilos para párrafos dentro del HTML parseado
                      margin: '0.5em 0',
                    },
                    '& strong': { // Estilos para texto en negrita
                      fontWeight: 600,
                    }
                  }}
                >
                  {parse(consulta.examen_fisico)}
                </Box>
              ) : (
                <Typography 
                  variant="body1" 
                  sx={{ 
                    whiteSpace: 'pre-wrap', 
                    pl: 2,
                    lineHeight: 1.7,
                    fontStyle: 'italic',
                    color: 'text.secondary',
                  }}
                >
                  No registrado
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Diagnósticos */}
          <Accordion 
            expanded={expanded.diagnosticos} 
            onChange={handleAccordionChange('diagnosticos')}
            sx={(theme) => sectionAccordionSx(theme, SECTION_ROLE['diagnosticos'], expanded.diagnosticos)}
          >
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />}
              sx={(theme) => sectionSummarySx(theme, SECTION_ROLE['diagnosticos'], expanded.diagnosticos)}
            >
              <SectionHeader icon={HealingIcon} title="Diagnósticos" role={SECTION_ROLE['diagnosticos']} />
            </AccordionSummary>
            <AccordionDetails sx={(theme) => sectionDetailsSx(theme, SECTION_ROLE['diagnosticos'])}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, pl: 1.5, borderLeft: (theme) => `3px solid ${theme.palette.primary.main}`, color: "primary.main" }}>
                  Diagnóstico Principal
                </Typography>
                <Typography variant="body1" sx={{                   pl: 2 }}>
                  {consulta.diagnostico_principal}
                </Typography>
              </Box>
              
              {consulta.diagnostico_secundario && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, pl: 1.5, borderLeft: (theme) => `3px solid ${theme.palette.primary.main}`, color: "primary.main" }}>
                    Diagnóstico Secundario
                  </Typography>
                  <Typography variant="body1" sx={{ pl: 2 }}>
                    {consulta.diagnostico_secundario}
                  </Typography>
                </Box>
              )}
              
              {consulta.diagnostico_terciario && (
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, pl: 1.5, borderLeft: (theme) => `3px solid ${theme.palette.primary.main}`, color: "primary.main" }}>
                    Diagnóstico Terciario
                  </Typography>
                  <Typography variant="body1" sx={{ pl: 2 }}>
                    {consulta.diagnostico_terciario}
                  </Typography>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
          
          {/* Reportes y Archivos Adjuntos */}
          <Accordion 
            expanded={expanded.reportes} 
            onChange={handleAccordionChange('reportes')}
            sx={(theme) => sectionAccordionSx(theme, SECTION_ROLE['reportes'], expanded.reportes)}
          >
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />}
              sx={(theme) => sectionSummarySx(theme, SECTION_ROLE['reportes'], expanded.reportes)}
            >
              <SectionHeader
                icon={DescriptionIcon}
                title="Reportes y Archivos Adjuntos"
                role={SECTION_ROLE['reportes']}
                chip={consulta.reportes && consulta.reportes.length > 0 && (
                  <Chip label={consulta.reportes.length} color="secondary" size="small" sx={{ ml: 1.5, fontWeight: 700 }} />
                )}
              />
            </AccordionSummary>
            <AccordionDetails sx={(theme) => sectionDetailsSx(theme, SECTION_ROLE['reportes'])}>
              <List sx={(theme) => ({ 
                bgcolor: 'background.paper',
                borderRadius: `${radius.sm}px`,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: alpha(theme.palette.divider, 0.6),
                boxShadow: softShadow,
                p: 0.5,
              })}>
                {consulta.reportes && consulta.reportes.length > 0 ? (
                  consulta.reportes.map((reporte) => (
                    <ListItem 
                      key={reporte.id_reporte}
                      secondaryAction={
                        <IconButton 
                          edge="end" 
                          aria-label="delete" 
                          onClick={() => handleDeleteFile(reporte.id_reporte)}
                          sx={{
                            '&:hover': {
                              color: 'error.main',
                              backgroundColor: 'rgba(211, 47, 47, 0.04)'
                            }
                          }}
                        >
                          <DeleteIcon color="error" />
                        </IconButton>
                      }
                      sx={{ 
                        transition: 'background-color 0.2s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.02)'
                        }
                      }}
                    >
                      <ListItemIcon>
                        <Box sx={(theme) => ({ ...sectionIconBoxSx(theme, 'secondary'), mr: 0 })}>
                          <DescriptionIcon fontSize="small" />
                        </Box>
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Link 
                            component="button" 
                            variant="body1" 
                            onClick={() => handleFileDownload(reporte.archivo, reporte.nombre_archivo)} 
                            sx={{ 
                              textAlign: 'left', 
                              fontWeight: '500',
                              color: 'primary.main',
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline'
                              }
                            }}
                          >
                            {reporte.nombre_archivo}
                          </Link>
                        }
                        secondary={`Subido el: ${new Date(reporte.fecha_subida).toLocaleDateString()}`}
                      />
                    </ListItem>
                  ))
                ) : (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No hay archivos adjuntos.
                    </Typography>
                  </Box>
                )}
              </List>
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-start' }}>
                <Button 
                  variant="outlined" 
                  component="label"
                  startIcon={<UploadFileIcon />}
                  sx={{
                    borderRadius: 999,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                    }
                  }}
                >
                  Subir Nuevo Archivo
                  <input type="file" hidden onChange={handleFileUpload} />
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Exámenes Laboratoriales */}
          <Accordion 
            expanded={expanded.laboratoriales} 
            onChange={handleAccordionChange('laboratoriales')}
            sx={(theme) => sectionAccordionSx(theme, SECTION_ROLE['laboratoriales'], expanded.laboratoriales)}
          >
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />}
              sx={(theme) => sectionSummarySx(theme, SECTION_ROLE['laboratoriales'], expanded.laboratoriales)}
            >
              <SectionHeader icon={BiotechIcon} title="Exámenes Laboratoriales" role={SECTION_ROLE['laboratoriales']} />
            </AccordionSummary>
            <AccordionDetails sx={(theme) => sectionDetailsSx(theme, SECTION_ROLE['laboratoriales'])}>
              <Typography 
                variant="body1" 
                sx={{ 
                  whiteSpace: 'pre-wrap', 
                  pl: 2,
                  lineHeight: 1.7,
                  fontStyle: consulta.examenes_laboratoriales ? 'normal' : 'italic',
                  color: consulta.examenes_laboratoriales ? 'text.primary' : 'text.secondary',
                }}
              >
                {consulta.examenes_laboratoriales || 'No registrados'}
              </Typography>
            </AccordionDetails>
          </Accordion>

          {/* Exámenes de Imagen */}
          <Accordion 
            expanded={expanded.imagen} 
            onChange={handleAccordionChange('imagen')}
            sx={(theme) => sectionAccordionSx(theme, SECTION_ROLE['imagen'], expanded.imagen)}
          >
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />}
              sx={(theme) => sectionSummarySx(theme, SECTION_ROLE['imagen'], expanded.imagen)}
            >
              <SectionHeader icon={ImageSearchIcon} title="Exámenes de Imagen" role={SECTION_ROLE['imagen']} />
            </AccordionSummary>
            <AccordionDetails sx={(theme) => sectionDetailsSx(theme, SECTION_ROLE['imagen'])}>
              <Typography 
                variant="body1" 
                sx={{ 
                  whiteSpace: 'pre-wrap', 
                  pl: 2,
                  lineHeight: 1.7,
                  fontStyle: consulta.examenes_imagen ? 'normal' : 'italic',
                  color: consulta.examenes_imagen ? 'text.primary' : 'text.secondary',
                }}
              >
                {consulta.examenes_imagen || 'No registrados'}
              </Typography>
            </AccordionDetails>
          </Accordion>

          {/* Plan de Tratamiento */}
          <Accordion 
            expanded={expanded.plan} 
            onChange={handleAccordionChange('plan')}
            sx={(theme) => sectionAccordionSx(theme, SECTION_ROLE['plan'], expanded.plan)}
          >
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />}
              sx={(theme) => sectionSummarySx(theme, SECTION_ROLE['plan'], expanded.plan)}
            >
              <SectionHeader icon={HealingIcon} title="Plan de Tratamiento" role={SECTION_ROLE['plan']} />
            </AccordionSummary>
            <AccordionDetails sx={(theme) => sectionDetailsSx(theme, SECTION_ROLE['plan'])}>
              <Typography 
                variant="body1" 
                sx={{ 
                  whiteSpace: 'pre-wrap', 
                  pl: 2,
                  lineHeight: 1.7,
                  fontStyle: consulta.plan_tratamiento ? 'normal' : 'italic',
                  color: consulta.plan_tratamiento ? 'text.primary' : 'text.secondary',
                }}
              >
                {consulta.plan_tratamiento || 'No registrado'}
              </Typography>
            </AccordionDetails>
          </Accordion>

          {/* Receta Médica */}
          <Accordion 
            expanded={expanded.receta} 
            onChange={handleAccordionChange('receta')}
            sx={(theme) => sectionAccordionSx(theme, SECTION_ROLE['receta'], expanded.receta)}
          >
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />}
              sx={(theme) => sectionSummarySx(theme, SECTION_ROLE['receta'], expanded.receta)}
            >
              <SectionHeader
                icon={MedicationIcon}
                title="Receta Médica"
                role={SECTION_ROLE['receta']}
                chip={consulta.medicamentos && consulta.medicamentos.length > 0 && (
                  <Chip label={consulta.medicamentos.length} color="secondary" size="small" sx={{ ml: 1.5, fontWeight: 700 }} />
                )}
              />
            </AccordionSummary>
            <AccordionDetails sx={(theme) => sectionDetailsSx(theme, SECTION_ROLE['receta'])}>
              {consulta.medicamentos && consulta.medicamentos.length > 0 ? (
                <List sx={(theme) => ({ 
                  bgcolor: 'background.paper',
                  borderRadius: `${radius.sm}px`,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: alpha(theme.palette.divider, 0.6),
                  boxShadow: softShadow,
                  p: 0.5,
                })}>
                  {consulta.medicamentos.map((med, index) => (
                    <React.Fragment key={med.id_medicamento}>
                      <ListItem 
                        sx={{ 
                          py: 2,
                          transition: 'background-color 0.2s ease',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.02)'
                          }
                        }}
                      >
                        <ListItemIcon>
                          <Box sx={(theme) => ({ ...sectionIconBoxSx(theme, 'secondary'), mr: 0 })}>
                            <MedicationIcon fontSize="small" />
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="subtitle1" fontWeight="medium">
                              {med.nombre} {med.nombre_comercial ? `(${med.nombre_comercial})` : ''} - {med.dosis}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              Indicación: {med.frecuencia}, por {med.duracion}
                              {med.notas && (
                                <Typography component="span" variant="body2" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                                  {med.notas}
                                </Typography>
                              )}
                            </Typography>
                          }
                        />
                      </ListItem>
                      {index < consulta.medicamentos.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No se ha registrado receta médica.
                  </Typography>
                </Box>
              )}
              <Box sx={{ mt: 3, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {hasPremiumAccess ? (
                  <>
                    {/* Selector de etiqueta (Normal/Controlados) si hay tags */}
                    {recetaTags.length > 0 && (
                      <>
                        <Typography variant="body2" color="text.secondary">Receta:</Typography>
                        <TextField
                          select
                          size="small"
                          label="Etiqueta"
                          value={selectedTagPdf}
                          onChange={(e) => setSelectedTagPdf(e.target.value)}
                          sx={{ minWidth: 160 }}
                        >
                          {recetaTags.map(t => (
                            <MenuItem key={t} value={t}>
                              {t === 'general' ? 'Normal' : t}
                            </MenuItem>
                          ))}
                        </TextField>
                      </>
                    )}

                    {/* Botón con firma/sello (siempre visible) */}
                    <Button
                      variant="outlined"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={downloadRecetaPDF}
                      sx={{ borderRadius: 999 }}
                    >
                      Receta PDF
                    </Button>

                    {/* Botón Interconsulta/Referencia */}
                    <Button
                      variant="filled"
                      color="secondary"
                      startIcon={<CompareArrowsIcon />}
                      onClick={() => setInterconsultaOpen(true)}
                      sx={{ borderRadius: 999 }}
                    >
                      Interconsulta
                    </Button>

                    {/* Botón sin firma (solo válido para controlados) */}
                   <Button
                      variant="outlined"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={async () => {
                        try {
                          const t = (selectedTagPdf || '').trim().toLowerCase();
                          const isControlado = ['controlados', 'controlado'].includes(t);
                          if (!isControlado) {
                            toast.info('La receta sin firma solo aplica a medicamentos “controlados”. Cambia la etiqueta a “controlados”.');
                            return;
                          }
                          const params = new URLSearchParams();
                          params.set('no_signature', '1');
                          params.set('receta_tag', t);
                          const url = `/consultas/${id}/generar-receta/?${params.toString()}`;
                          const response = await axiosInstance.get(url, { responseType: 'blob' });
                          const blob = new Blob([response.data], { type: 'application/pdf' });
                          const blobUrl = window.URL.createObjectURL(blob);
                          window.open(blobUrl, '_blank', 'noopener,noreferrer');
                          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
                        } catch (error) {
                          try {
                            const text = await error?.response?.data?.text?.();
                            if (text) {
                              const json = JSON.parse(text);
                              if (json?.error) {
                                toast.error(json.error);
                                return;
                              }
                            }
                          } catch (_) {}
                          if (error?.response?.status === 403) {
                            toast.error('Esta función requiere un plan premium');
                          } else if (error?.response?.status === 404) {
                            toast.error('Consulta no encontrada o sin permisos');
                          } else {
                            toast.error('No se pudo generar la receta sin firma.');
                          }
                        }
                      }}
                      sx={{ borderRadius: 999 }}
                    >
                      Receta PDF (sin firma)
                    </Button>

                    {/* Enviar por correo */}
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={openEmailDialog}
                      sx={{
                        borderRadius: 999,
                        transition: 'all 0.2s ease',
                        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 10px rgba(0,0,0,0.15)' },
                      }}
                    >
                      Enviar receta por correo
                    </Button>
                  </>
                ) : (
                  <>
                    <PremiumFeatureBadge>
                      <Button variant="outlined" startIcon={<PictureAsPdfIcon />} disabled sx={{ borderRadius: 999 }}>
                        Receta PDF
                      </Button>
                    </PremiumFeatureBadge>
                    <PremiumFeatureBadge>
                      <Button variant="outlined" startIcon={<PictureAsPdfIcon />} disabled sx={{ borderRadius: 999 }}>
                        Receta PDF (sin firma)
                      </Button>
                    </PremiumFeatureBadge>
                    <PremiumFeatureBadge>
                      <Button variant="contained" color="primary" disabled sx={{ borderRadius: 999 }}>
                        Enviar receta por correo
                      </Button>
                    </PremiumFeatureBadge>
                  </>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Fade>
      <Dialog open={emailDialogOpen} onClose={closeEmailDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Enviar receta por correo</DialogTitle>
        <DialogContent dividers>
          <FormControl component="fieldset" sx={{ width: '100%' }}>
            <FormLabel component="legend">Selecciona el destinatario</FormLabel>
            <RadioGroup
              value={emailOption}
              onChange={(e) => {
                setEmailOption(e.target.value);
                setEmailError('');
              }}
            >
              <FormControlLabel
                value="registered"
                control={<Radio />}
                disabled={!patientEmail}
                label={
                  patientEmail
                    ? `Correo del paciente: ${patientEmail}`
                    : 'Correo del paciente no registrado'
                }
              />
              <FormControlLabel
                value="custom"
                control={<Radio />}
                label="Enviar a un correo diferente"
              />
            </RadioGroup>

            {emailOption === 'custom' && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  label="Correo destinatario"
                  placeholder="nombre@dominio.com"
                  fullWidth
                  value={customEmail}
                  onChange={(e) => setCustomEmail(e.target.value)}
                  error={!!emailError}
                  helperText={emailError || ''}
                  autoFocus
                  inputProps={{ inputMode: 'email' }}
                />
              </Box>
            )}

            {emailOption === 'registered' && !!emailError && (
              <FormHelperText error sx={{ mt: 1 }}>
                {emailError}
              </FormHelperText>
            )}
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEmailDialog} disabled={sendingEmail}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSendRecetaEmail}
            disabled={sendingEmail || (emailOption === 'registered' && !patientEmail)}
          >
            {sendingEmail ? (
              <>
                Enviando…
                <CircularProgress size={18} sx={{ ml: 1, color: 'inherit' }} />
              </>
            ) : (
              'Enviar'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Interconsulta/Referencia */}
      <Dialog open={interconsultaOpen} onClose={() => setInterconsultaOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Generar Interconsulta / Referencia Médica</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre del Doctor"
                value={interconsultaData.nombreDoctor}
                onChange={(e) => setInterconsultaData(prev => ({ ...prev, nombreDoctor: e.target.value }))}
                placeholder="Dr./Dra. Nombre del especialista"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Especialidad"
                value={interconsultaData.especialidad}
                onChange={(e) => setInterconsultaData(prev => ({ ...prev, especialidad: e.target.value }))}
                placeholder="Ej: Cardiología, Neurología, etc."
              />
            </Grid>
            
            {/* Historia de la Enfermedad Actual */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Historia de la Enfermedad Actual / Motivo de Consulta
                </Typography>
                <Button
                  size="small"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => {
                    // Strip HTML tags from anamnesis for plain text
                    const strippedAnamnesis = consulta?.anamnesis?.replace(/<[^>]*>/g, '') || '';
                    setInterconsultaData(prev => ({ 
                      ...prev, 
                      historiaEnfermedad: prev.historiaEnfermedad 
                        ? prev.historiaEnfermedad + '\n\n' + strippedAnamnesis 
                        : strippedAnamnesis 
                    }));
                    toast.success('Anamnesis agregada');
                  }}
                  disabled={!consulta?.anamnesis}
                >
                  Copiar Anamnesis
                </Button>
              </Box>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={interconsultaData.historiaEnfermedad}
                onChange={(e) => setInterconsultaData(prev => ({ ...prev, historiaEnfermedad: e.target.value }))}
                placeholder="Describa la historia de la enfermedad actual o copie la anamnesis..."
              />
            </Grid>

            {/* Tratamiento actual */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Tratamiento actual
                </Typography>
                <Button
                  size="small"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => {
                    // Copiar medicamentos de la receta
                    const medicamentosText = (consulta?.medicamentos || []).map(med => {
                      let text = `- ${med.nombre}`;
                      if (med.nombre_comercial) text += ` (${med.nombre_comercial})`;
                      text += ` - ${med.dosis}`;
                      text += `\n  Indicaci\u00f3n: ${med.frecuencia}, por ${med.duracion}`;
                      if (med.notas) text += `\n  Notas: ${med.notas}`;
                      return text;
                    }).join('\n\n');
                    
                    setInterconsultaData(prev => ({ 
                      ...prev, 
                      tratamiento: prev.tratamiento 
                        ? prev.tratamiento + '\n\n' + medicamentosText 
                        : medicamentosText 
                    }));
                    toast.success('Receta m\u00e9dica agregada');
                  }}
                  disabled={!consulta?.medicamentos || consulta.medicamentos.length === 0}
                >
                  Copiar Receta
                </Button>
              </Box>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={interconsultaData.tratamiento}
                onChange={(e) => setInterconsultaData(prev => ({ ...prev, tratamiento: e.target.value }))}
                placeholder="Tratamiento actual del paciente o copie la receta..."
              />
            </Grid>

              {/* Correo del destinatario (opcional) */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Enviar por correo electrónico (opcional)
                </Typography>
                <TextField
                  fullWidth
                  type="email"
                  label="Correo del destinatario"
                  placeholder="doctor@ejemplo.com"
                  value={interconsultaData.email}
                  onChange={(e) => setInterconsultaData(prev => ({ ...prev, email: e.target.value }))}
                  inputProps={{ inputMode: 'email' }}
                  helperText="Si ingresas un correo, podrás enviar el PDF directamente al destinatario."
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Button onClick={() => setInterconsultaOpen(false)}>Cancelar</Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<EmailIcon />}
              disabled={
                enviandoRefEmail ||
                generandoRefPDF ||
                !interconsultaData.nombreDoctor ||
                !interconsultaData.especialidad ||
                !interconsultaData.email ||
                !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(interconsultaData.email).trim())
              }
              onClick={async () => {
                setEnviandoRefEmail(true);
                const loadingToast = toast.loading('Enviando referencia por correo...');
                try {
                  await axiosInstance.post(
                    `/consultas/${id}/enviar-referencia/`,
                    {
                      email: interconsultaData.email.trim(),
                      doctor_referido: interconsultaData.nombreDoctor,
                      especialidad_referido: interconsultaData.especialidad,
                      motivo_referencia: interconsultaData.historiaEnfermedad,
                      historia_enfermedad: interconsultaData.historiaEnfermedad,
                      tratamiento_actual: interconsultaData.tratamiento,
                      diagnostico: consulta?.diagnostico_principal || '',
                    }
                  );
                  toast.update(loadingToast, {
                    render: 'Referencia enviada por correo correctamente',
                    type: 'success',
                    isLoading: false,
                    autoClose: 3000,
                  });
                  setInterconsultaOpen(false);
                  setInterconsultaData({ nombreDoctor: '', especialidad: '', historiaEnfermedad: '', tratamiento: '', email: '' });
                } catch (error) {
                  console.error('Error al enviar referencia por correo:', error);
                  toast.update(loadingToast, {
                    render: error.response?.status === 403
                      ? 'Esta función requiere un plan premium'
                      : (error.response?.data?.error || error.response?.data?.detail || 'No se pudo enviar la referencia por correo'),
                    type: 'error',
                    isLoading: false,
                    autoClose: 5000,
                  });
                } finally {
                  setEnviandoRefEmail(false);
                }
              }}
            >
              {enviandoRefEmail ? 'Enviando...' : 'Enviar por correo'}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              disabled={generandoRefPDF || !interconsultaData.nombreDoctor || !interconsultaData.especialidad}            onClick={async () => {
              setGenerandoRefPDF(true);
              const loadingToast = toast.loading("Generando PDF de referencia...");
              try {
                const params = new URLSearchParams();
                params.set('doctor_referido', interconsultaData.nombreDoctor);
                params.set('especialidad_referido', interconsultaData.especialidad);
                params.set('motivo_referencia', interconsultaData.historiaEnfermedad);
                params.set('historia_enfermedad', interconsultaData.historiaEnfermedad);
                params.set('tratamiento_actual', interconsultaData.tratamiento);
                params.set('diagnostico', consulta?.diagnostico_principal || '');
                
                const response = await axiosInstance.get(
                  `/referencia-pdf/${consulta.paciente_id}/?${params.toString()}`,
                  { responseType: 'blob' }
                );
                
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `referencia_${consulta.paciente_id}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                
                toast.update(loadingToast, {
                  render: "Referencia PDF descargada correctamente",
                  type: "success",
                  isLoading: false,
                  autoClose: 3000
                });
                setInterconsultaOpen(false);
                setInterconsultaData({ nombreDoctor: '', especialidad: '', historiaEnfermedad: '', tratamiento: '', email: '' });
              } catch (error) {
                console.error("Error al generar referencia PDF:", error);
                toast.update(loadingToast, {
                  render: error.response?.status === 403
                    ? "Esta función requiere un plan premium"
                    : "Error al generar la referencia PDF",
                  type: "error",
                  isLoading: false,
                  autoClose: 5000
                });
              } finally {
                setGenerandoRefPDF(false);
              }
            }}
          >
            {generandoRefPDF ? (
              <>
                Generando...
                <CircularProgress size={18} sx={{ ml: 1, color: 'inherit' }} />
              </>
            ) : (
              'Generar PDF'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConsultaDetailPage;